import * as moment from 'moment-timezone';
import 'moment/locale/fr-ca';
import { ServiceInfo, ServiceMetaInfo } from './shared-constants';
import { DurationInputArg2 } from 'moment';

export enum TimeUnit {
	Minute = 'Minute',
	Hour = 'Hour',
	Day = 'Day',
	Week = 'Week',
	Month = 'Month',
	Counter = 'Counter'
}

export enum ServiceType {
	Private = 'Privé',
	PrivateWithApprobation = 'Privé avec approbation',
	Public = 'Public',
	PublicWithApprobation = 'Public avec approbation'
}

export enum ServiceStartRule {
	NotDefined = 'Inconnu',
	FirstDayOfNextMonth = 'Premier jour du prochain mois',
	SubscriptionDay = "Jour de l'inscription"
}

export enum PartialMonthCalculation {
	NotDefined = 'Inconnu',
	Ratio = 'Ratio',
	MiddleMonth = 'Middle Month'
}

export enum ServiceBillingType {
	Monthly = 'Mensuel',
	PerUse = "À l'utilisation",
	AtBooking = 'Au moment de la réservation',
	Decremental = "À l'utilisation (décompte)",
	Maintenance = '',
	LumpSum = 'Montant forfaitaire',
	Episodic = 'Épisodique',
	DAS = 'DAS (Déduction à la source)'
}

export interface PaymentInfo {
	amount: number;
	contractNo: number;
	financialInstitution: string;
	id: number;
	invoicePayments: InvoicePaymentInfo[];
	noReference: string;
	paymentMethodDescription: string;
	processDate: string;
}

export interface InvoicePaymentInfo {
	invoiceAmount: number;
	invoiceDate: string;
	invoiceDescription: string;
	invoiceId: number;
	invoiceTax1: number;
	invoiceTax2: number;
	invoiceTotalAmount: number;
	paidAmount: number;
}

export class Service {
	get availableFrom() {
		return this._data.availableFrom;
	}

	get availableFromFormated() {
		return moment(this.availableFrom).tz('America/Montreal').format('LL');
	}

	get availableServiceId() {
		return this._data.availableServiceId;
	}

	get availableUntil() {
		return this._data.availableUntil;
	}

	get isExpired() {
		return moment(this.availableUntil).toDate().getTime() < moment().toDate().getTime();
	}

	get availableUntilFormated() {
		if (moment(this.availableUntil).get('year') === 9999) {
			return 'Indéterminée';
		}
		return moment(this.availableUntil).tz('America/Montreal').format('LL');
	}

	get cancelationNotice() {
		return this._data.cancelationNotice;
	}

	get cancelationNoticeTimeUnit() {
		return this._data.cancelationNoticeTimeUnit;
	}

	get cancelationRule() {
		if (!this.isCancelable) {
			return 'Impossible';
		}
		if (this.isCancelationFirstDayOfMonth) {
			return 'Premier jour du mois suivant';
		}
		return `${this._data.cancelationNotice} ${this._data.cancelationNoticeTimeUnit}`;
	}

	get duration() {
		return this._data.duration;
	}

	get durationTime() {
		return this.isFixedDuration ? `${this.duration} ${this.durationTimeUnit}` : 'Indéterminée';
	}

	get durationTimeUnit() {
		return this._data.durationTimeUnit;
	}
	get durationTimeUnitMoment() {
		if (this._data.durationTimeUnit === 'Day') {
			return 'days';
		}
		if (this._data.durationTimeUnit === 'Week') {
			return 'weeks';
		}
		if (this._data.durationTimeUnit === 'Month') {
			return 'months';
		}
		return 'days';
	}

	get formattedDurationTimeUnit() {
		let timeValue = '';
		if (this._data.durationTimeUnit === 'Week') {
			timeValue = 'semaine';
			if (this._data.duration > 1) {
				timeValue += 's';
			}
		} else if (this._data.durationTimeUnit === 'Day') {
			timeValue = 'jour';
			if (this._data.duration > 1) {
				timeValue += 's';
			}
		} else if (this._data.durationTimeUnit === 'Month') {
			timeValue = 'mois';
		}

		return timeValue;
	}

	get endDate() {
		const endDate = moment(this.onSaleUntil).tz('America/Montreal');

		return endDate;
	}

	get endDateFormatted() {
		if (
			this.period &&
			(this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.durationTimeUnit === 'Week'))
		) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day').format('LL');
		}
		if (moment(this.endDate).get('year') === 9999) {
			return 'Indéterminée';
		}
		return this.isFixedDuration ? moment(this.endDate).tz('America/Montreal').format('LL') : 'Indéterminée';
	}

	get havePartialAmmount() {
		if (this.price === 0) {
			return false;
		}
		if (
			((this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') ||
				(this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic')) &&
			((this.rawData.serviceStartRule && this.rawData.serviceStartRule.toString() === 'FirstDayOfNextMonth') ||
				(this.rawData.serviceStartRule && this.rawData.serviceStartRule.toString() === 'FirstDayOfCurrentMonth'))
		) {
			return false;
		}
		return true;
	}

	get isApprovable() {
		return (
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'PublicWithApprobation') ||
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'PrivateWithApprobation')
		);
	}

	get isBankingCardPaymentAccepted() {
		return this._data.isBankingCardPaymentAccepted;
	}

	get isCancelable() {
		return this._data.isCancelable;
	}

	get isCancelationFirstDayOfMonth() {
		return this._data.isCancelationFirstDayOfMonth;
	}

	get isDAS() {
		return (
			(this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'DAS') ||
			(this.rawData.isDasPaymentAccepted &&
				!this.rawData.isPpaPaymentAccepted &&
				!this.rawData.isBankingCardPaymentAccepted &&
				!this.rawData.isFinancialInstitutionPaymentAccepted)
		);
	}

	get isDasPaymentAccepted() {
		return this._data.isDasPaymentAccepted;
	}

	get isEpisodic() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic';
	}

	get isFinancialInstitutionPaymentAccepted() {
		return this._data.isFinancialInstitutionPaymentAccepted;
	}

	get isFixedDuration() {
		return this._data.isFixedDuration;
	}

	get isMonthly() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly';
	}

	get isPerUse() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'PerUse';
	}
	get isAtBooking() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'AtBooking';
	}

	get isDecremental() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Decremental';
	}

	get isLumpSum() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum';
	}

	get isPpaPaymentAccepted() {
		return this._data.isPpaPaymentAccepted;
	}

	get isPrivate() {
		return (
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'Private') ||
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'PrivateWithApprobation')
		);
	}

	get isSubscriptionAvailable() {
		return this._data.isSubscriptionAvailable;
	}

	get isTax1Exempted() {
		return this._data.isTax1Exempted;
	}

	get isTax2Exempted() {
		return this._data.isTax2Exempted;
	}

	get isTax3Exempted() {
		return this._data.isTax3Exempted;
	}

	get meta(): ServiceMetaInfo {
		return this._data.meta || { note: null };
	}

	get onSaleFrom() {
		return this._data.onSaleFrom;
	}

	get onSaleFromFormated() {
		return moment(this.onSaleFrom).tz('America/Montreal').format('LL');
	}

	get onSaleUntil() {
		return this._data.onSaleUntil;
	}

	get onSaleUntilFormated() {
		if (
			this.rawData.serviceBillingType &&
			this.rawData.serviceBillingType.toString() === 'LumpSum' &&
			this.durationTimeUnit === 'Week'
		) {
			if (this.duration) {
				const durationTimeUnit: DurationInputArg2 = this.rawData.durationTimeUnit as DurationInputArg2;
				const duration: number = Number(this.rawData.duration);
				const end = moment(this.onSaleFrom)
					.clone()
					// @ts-ignore
					.add(duration, durationTimeUnit)
					.endOf('day')
					.format('LL');

				return end;
			}
		}
		if (moment(this.onSaleUntil).get('year') === 9999) {
			return 'Indéterminée';
		}
		return moment(this.onSaleUntil).tz('America/Montreal').format('LL');
	}

	get parkIdentificationNo() {
		return this._data.parkIdentificationNo;
	}

	get partialEndDate() {
		if (this.period && this.period.force) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		if (
			this.period &&
			this._data.serviceBillingType.toString() === 'LumpSum' &&
			(this.durationTimeUnit === 'Week' || this.durationTimeUnit === 'Day')
		) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') {
			if (!this.isFixedDuration) {
				return moment(this.onSaleUntil).tz('America/Montreal');
			} else {
				return this.startDate.add(1, 'month').startOf('month').subtract(1, 'day').endOf('day').tz('America/Montreal');
			}
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic') {
			if (this.period) {
				return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
			} else {
				return moment(this.rawData.onSaleUntil).tz('America/Montreal').endOf('day');
			}
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'DAS') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}

		return moment(this.onSaleUntil).tz('America/Montreal');
	}

	get partialEndDateFormatted() {
		return this.partialEndDate.format('LL');
	}

	get partialInvoiceToDate() {
		if (this.period && this.period.force) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') {
			return this.startDate.clone().add(1, 'month').startOf('month').subtract(1, 'day').endOf('day').tz('America/Montreal');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic') {
			return this.partialEndDate;
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum') {
			if (this.period && (this.durationTimeUnit === 'Week' || this.durationTimeUnit === 'Day')) {
				return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
			} else {
				return moment(this.onSaleUntil).tz('America/Montreal');
			}
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'DAS') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'PerUse') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}
		return this.startDate;
	}

	get partialInvoiceToDateFormatter() {
		return this.partialInvoiceToDate.format('LL');
	}

	get partialMonthCalculation() {
		return this._data.partialMonthCalculation;
	}

	get partialPrice() {
		const currentDay = this.startDate.date();
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'PerUse') {
			return this.rawData.price || 0;
		}
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum') {
			if (this.serviceStartRule === 'FirstDayOfFlatRateDuration') {
				return this.rawData.price || 0;
			}
			if (
				this.serviceStartRule === 'SubscriptionDay' &&
				this.period &&
				this.period.startDateRaw &&
				this.period.endDateRaw &&
				this.rawData.price > 0
			) {
				const days =
					Math.ceil(
						moment(this.period.endDateRaw)
							.clone()
							.endOf('day')
							.diff(moment(this.period.startDateRaw).clone().startOf('day'), 'days', true)
					) - 1;
				return this.rawData.price * days || 0;
			}
			const availableFrom = moment(this.rawData.onSaleFrom).tz('America/Montreal');
			const availableUntil = moment(this.rawData.onSaleUntil).tz('America/Montreal');

			const countMonths = Math.ceil(availableUntil.endOf('month').diff(this.startDate.clone().startOf('month'), 'months', true));

			const duration = this.rawData.duration || 1;
			const amount = this.rawData.price || 0;

			const priceByMonth = amount / duration;

			const periodFrom = this.startDate.clone().startOf('months');

			if (
				(this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'Ratio') ||
				(this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'MiddleMonth')
			) {
				if (this.period && this.durationTimeUnit === 'Week') {
					return parseFloat((this.price * (this.period.months || 1)).toString()).toFixed(2);
				}
				let cost = Number(priceByMonth);

				const daysInMonth = this.startDate.daysInMonth();
				const daysToPay = daysInMonth - currentDay + 1;

				const total = countMonths * Number(priceByMonth);

				cost = total;

				return parseFloat(cost.toString()).toFixed(2);
			}
			if (this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'OldMiddleMonth') {
				let cost = 0;
				if (currentDay >= 15) {
					cost = Number((Number(priceByMonth) / 2).toFixed(2));
				} else {
					cost = Number(priceByMonth);
				}

				cost = Number(cost) + countMonths * Number(priceByMonth);

				return parseFloat(cost.toString()).toFixed(2);
			}

			return parseFloat(amount.toString()).toFixed(2);
		}
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') {
			let billingDate = moment(this.startDate);
			let ApplyMiddleMonth = false;
			if (this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'MiddleMonth') {
				if (currentDay >= 1 && currentDay <= 14) {
					billingDate = billingDate.clone().add('days', currentDay * -1 + 1);
				} else {
					ApplyMiddleMonth = true;
					billingDate = billingDate.clone().add('days', currentDay * -1 + 15);
				}
			}
			const daysInMonth = billingDate.daysInMonth() || 0;
			const daysToPay = daysInMonth - currentDay + 1 || 0;

			let amount = (daysToPay / daysInMonth) * (this.rawData.price || 0);

			if (ApplyMiddleMonth) {
				amount = (this.rawData.price || 0) / 2;
			}

			return parseFloat(amount.toString()).toFixed(2);
		}
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic') {
			if (this.period) {
				return parseFloat((this.price * (this.period.months || 1)).toString()).toFixed(2);
			} else {
				const start = moment();
				if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
					start.endOf('month').add(1, 'day').startOf('day');
				}
				if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
					start.startOf('month');
				}

				const availableUntil = moment(this.rawData.onSaleUntil).tz('America/Montreal');

				const totalMonths = Math.ceil(availableUntil.diff(start, 'month', true));

				return parseFloat((this.price * totalMonths).toString()).toFixed(2);
			}
		}
		return 0;
	}

	get period() {
		return this._data.period;
	}

	get price(): number {
		return Number(parseFloat((this._data && this._data.price && this._data.price.toString()) || '0'));
	}

	get rawData() {
		return this._data || ({} as ServiceInfo);
	}

	get sectorId() {
		return this._data.sectorId;
	}

	get serviceBillingType() {
		if (this._data && this._data.serviceBillingType) {
			return ServiceBillingType[this._data && this._data.serviceBillingType];
		} else {
			return '';
		}
	}

	get serviceDescription() {
		return this._data.serviceDescription;
	}

	get serviceStartRule() {
		return this._data.serviceStartRule;
	}

	get serviceType() {
		return this._data.serviceType;
	}

	get startDate() {
		if (this.period && this.period.cem) {
			return moment(this.period.startDateRaw).tz('America/Montreal');
		}
		if (
			this.period &&
			(this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.durationTimeUnit === 'Week'))
		) {
			return moment(this.period.startDateRaw).tz('America/Montreal');
		}
		const now = moment(new Date(this.availableFrom).getTime() > this.today.toDate().getTime() ? this.availableFrom : this.today);

		if (this._data && this._data.serviceBillingType && this._data.serviceBillingType.toString() === 'Episodic') {
			if (this.period) {
				return moment(this.period.startDateRaw).tz('America/Montreal');
			}
		}
		if (this._data && this._data.serviceBillingType && this._data.serviceBillingType.toString() === 'Monthly') {
			if (this.period) {
				return moment(this.period.startDateRaw).tz('America/Montreal');
			}
		}
		if (this._data && this._data.serviceBillingType && this._data.serviceBillingType.toString() === 'DAS') {
			if (this.period) {
				return moment(this.period.startDateRaw).tz('America/Montreal');
			}
		}
		if (this._data && this._data.serviceStartRule && this._data.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			return now.clone().add(1, 'months').startOf('month').tz('America/Montreal');
		}
		if (this._data && this._data.serviceStartRule && this._data.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
			return now.clone().startOf('month').tz('America/Montreal');
		}
		return now.tz('America/Montreal');
	}

	get startDateFormatted() {
		if (
			this.period &&
			(this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.durationTimeUnit === 'Week'))
		) {
			return moment(this.period.startDateRaw).tz('America/Montreal');
		}
		return moment(this.startDate).tz('America/Montreal').format('LL');
	}

	today = moment().tz('America/Montreal').startOf('day');

	private _data: ServiceInfo = {
		availableServiceId: 0,
		cancelationNotice: 0,
		cancelationNoticeTimeUnit: TimeUnit.Month,
		duration: 0,
		durationTimeUnit: TimeUnit.Month,
		isBankingCardPaymentAccepted: false,
		isCancelable: false,
		isCancelationFirstDayOfMonth: false,
		isDasPaymentAccepted: false,
		isFinancialInstitutionPaymentAccepted: false,
		isFixedDuration: false,
		isPpaPaymentAccepted: false,
		isSubscriptionAvailable: false,
		isTax1Exempted: false,
		isTax2Exempted: false,
		isTax3Exempted: false,
		parkIdentificationNo: 0,
		partialMonthCalculation: 'NotDefined',
		price: 0,
		sectorId: 0,
		priceListingInfo: [],
		serviceBillingType: 'Monthly',
		serviceDescription: '',
		serviceStartRule: 'NotDefined',
		serviceType: 'Private',
		availableFrom: new Date().toISOString(),
		availableUntil: new Date().toISOString(),
		onSaleFrom: new Date().toISOString(),
		onSaleUntil: new Date().toISOString(),
		period: null,
		meta: null,
		webEffectiveDate: null,
		webEffectiveHour: null,
		webEndDate: null,
		webEndHour: null
	};

	constructor(data: ServiceInfo) {
		if (data) {
			Object.keys(data).forEach(k => {
				if (this._data[k] !== undefined) {
					this._data[k] = data[k];
				}
			});
		}
	}

	isSubscriptionDay() {
		if (this.serviceStartRule && this.serviceStartRule.toString() === 'SubscriptionDay') {
			return true;
		}
		return false;
	}

	getPricingPeriods(includeBefore: boolean = false) {
		const filteredPriceListingInfo = this._data.priceListingInfo.filter(item => {
			const effectiveDate = moment(item.effectiveDate);
			const currentDate = moment();
			const futureDate = moment().add(31, 'days');

			// Exclure les entrées dont la date effective est antérieure à la date d'aujourd'hui

			return effectiveDate.isSameOrBefore(futureDate) && effectiveDate.isSameOrAfter(currentDate);
		});

		filteredPriceListingInfo.forEach(item => {
			const effectiveDate = moment(item.effectiveDate);
			const formattedDate = effectiveDate.format('DD MMMM YYYY');

			item.effectiveDateFormated = `à partir du ${formattedDate}`;
			item.price = parseFloat(item.price.toString()).toFixed(2);
		});

		return filteredPriceListingInfo;
	}
	getAvailablePeriods(ss: any = null, isScope = true) {
		const periods: any[] = [];

		const endAt = moment(this.onSaleUntil);
		const start = moment(ss ? ss.startDateRaw : this.startDate) || moment();
		const currentDay = new Date().getDate();

		if (!ss && !isScope && !this.isDecremental) {
			const toDay = moment();
			return [
				{
					value: 0,
					start: toDay.format('LL'),
					end: toDay.format('LL'),
					months: 1,
					endDateRaw: toDay.toISOString() as string,
					startDateRaw: toDay.toISOString() as string
				},
				{
					value: 1,
					start: toDay.clone().endOf('month').add(1, 'day').startOf('month').format('LL'),
					end: toDay.clone().endOf('month').add(1, 'day').startOf('month').format('LL'),
					months: 2,
					endDateRaw: toDay.clone().endOf('month').add(1, 'day').startOf('month').toISOString() as string,
					startDateRaw: toDay.clone().endOf('month').add(1, 'day').startOf('month').toISOString() as string
				}
			];
		}

		if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			if (start.toDate().getTime() < new Date().getTime()) {
				start.clone().endOf('month').add(1, 'day').startOf('day');
			}
		}
		if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
			start.startOf('month');
		}

		if (!isScope && start.format('M') === moment().format('M') && Number(start.format('D')) > 15) {
			start.clone().add(1, 'month').startOf('month');
		}

		let months = Math.ceil(endAt.diff(start, 'month', true));

		if (!ss && months >= 2) {
			if (this.startDate.toDate().getTime() < new Date().getTime()) {
				months = 3;
			} else {
				months = 2;
			}
		}

		for (let i = 0; i < Math.min(months, 120); i++) {
			let end = start.clone().add(i, 'months').endOf('month');

			if (this.durationTimeUnit === 'Week' && this.duration > 0) {
				const durationTimeUnit: DurationInputArg2 = this.rawData.durationTimeUnit as DurationInputArg2;
				const duration: number = Number((i + 1) * Number(this.rawData.duration));
				end = start
					.clone()
					// @ts-ignore
					.add(duration, durationTimeUnit)
					.endOf('day');
			}

			if (
				this.durationTimeUnit === 'Day' &&
				this.duration > 0 &&
				this.isLumpSum &&
				this.serviceStartRule.toString() === 'SubscriptionDay'
			) {
				const durationTimeUnit: DurationInputArg2 = this.rawData.durationTimeUnit as DurationInputArg2;
				const duration: number = Number((i + 1) * Number(this.rawData.duration));
				end = start
					.clone()
					// @ts-ignore
					.add(duration, 'days')
					.endOf('day');
			}

			let s = start.clone().add(i, 'months').startOf('month');

			if (!i && this.serviceStartRule && this.serviceStartRule.toString() === 'SubscriptionDay') {
				if (this.startDate.toDate().getTime() < new Date().getTime()) {
					s = moment().startOf('day');
				} else {
				}
			}

			const sss = (ss && start.toISOString()) || s.toISOString();
			periods.push({
				value: i,
				start: (ss && start.format('LL')) || s.format('LL'),
				end: moment(end).format('LL'),
				months: moment(end).diff(moment(sss), 'months') + 1,
				endDateRaw: moment(end).toISOString() as string,
				endDateRaw3_: moment(end).toISOString() as string,
				startDateRaw: sss as string
			});
		}
		return periods.slice(0, 24);
	}

	set(name, value) {
		const newData = { ...this._data };

		newData[name] = value;

		this._data = newData;
	}

	setToday(date) {
		this.today = moment(date).tz('America/Montreal').startOf('day');
	}
}
